import React, { useState } from 'react';
import LogoIcon from 'components/LogoIcon';
import { Col, Dropdown, Row } from 'antd';
import styled from 'styled-components';
import { CaretDownOutlined } from '@ant-design/icons';
import { useStandardExams } from 'store/standardexams';
import { find, get } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { isAfterTime, isBeforTime } from 'utils/methods/math';
import moment from 'moment-timezone';
import { useOrgTimezone } from 'store/organizations/selectors';
import { Alert, Snackbar } from '@mui/material';
import { connect } from 'react-redux';
import { useDemoStatus } from 'store/users/selectors';
import { BackButtonEbook } from 'components/UI/buttons/Backbutton';
import SentryWrap from 'components/Sentry';

const NavigationItem = styled.li`
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: flex-start;
  list-style: none;
  padding: 0;
  margin: 0;
`;
const HeaderActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
  backdrop-filter: blur(10px);
  flex-wrap: wrap;
  min-width: 300px;
`;
const PaddedBtn = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  outline: none;
  padding: 8px 10px;
  text-align: left;
  width: 100%;
  border-radius: 10px;
  color: white;
  transition: background-color 150ms ease;
  &:hover {
    background-color: rgba(255, 255, 255, 0.14);
  }
`;
const ButtonLink = styled.button`
  align-items: center;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  cursor: pointer;
  display: inline-flex;
  flex-direction: row;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 700;
  outline: none;
  color: white;
  white-space: wrap;
  justify-content: center;
  text-align: center;
  min-width: 210px;
  transition: background 150ms ease, border-color 150ms ease, transform 150ms ease;
  &:hover {
    background: rgba(255, 255, 255, 0.18);
    border-color: rgba(255, 255, 255, 0.28);
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0px) scale(0.99);
  }
`;

const ViewReportButton = styled(ButtonLink)`
  background: linear-gradient(135deg, rgba(88, 42, 245, 0.85), rgba(0, 210, 255, 0.55));
  border-color: rgba(88, 42, 245, 0.55);
  box-shadow: 0 14px 30px rgba(88, 42, 245, 0.22);
  &:hover {
    background: linear-gradient(135deg, rgba(88, 42, 245, 0.95), rgba(0, 210, 255, 0.7));
    border-color: rgba(170, 135, 255, 0.7);
  }
`;

const noAssignedMsg = "Not Assigned by Teacher"

const StandardBasedExam = ({ classActivities, classQuizzes, currentUser }) => {
  const navigate = useNavigate();
  const orgTimezone = useOrgTimezone();
  const AllstandardExam = useStandardExams(); // dynamic data
  const demoStatus = useDemoStatus();
  const isReviewAccount = currentUser?.reviewAccount || false
  const isStudent = currentUser?.role === "student" || false
  const standardExams = AllstandardExam.sort((a, b) => Number(a.order) - Number(b.order));
  const [state, setState] = React.useState({
    open: false,
    message: '',
  });
  const [activeGrade, setActiveGrade] = useState(
    standardExams.length > 0 ? standardExams[0].displayName : null,
  );

  const handleSelectGrade = (grade) => {
    setActiveGrade(grade);
  };

  const handleClose = () => {
    setState({ open: false, message: '' });
  };

  const showMsg = (text) => {
    setState({ open: true, message: text });
  };

  const showCopyRight = () => {
    setState({ open: true, message: 'Restricted access (legend)' });
  };

  const onCoverCLick = (obj) => {
    const chapter = get(obj, 'bookId');
    if (demoStatus || isReviewAccount) {
      showCopyRight();
      return;
    }
    const url = `/ebook?chapter=${chapter}&page=1&activity=null&quiz=null`;
    
    const activitiy = find(classActivities, (k) => get(k, 'data.bookId') == chapter);
    const quiz = find(classQuizzes, (k) => get(k, 'data.bookId') == chapter);
    if (isStudent) {
      if (!activitiy && !quiz) {
        showMsg(noAssignedMsg);
        return;
      } else {
        navigate(url);
      }
    } else {
      const startDate = get(obj, 'startDate');
      const endDate = get(obj, 'endDate');
      if (startDate && endDate) {
        if (isAfterTime(startDate, moment(), orgTimezone)) {
          showMsg(` Available by ${moment(startDate).format('MM/DD/YYYY hh:mm A')}`);
        } else if (isBeforTime(endDate, moment(), orgTimezone)) {
          showMsg(` Exam is ended on ${moment(endDate).format('MM/DD/YYYY hh:mm A')}`);
        } else {
          navigate(url);
        }
      } else if (startDate) {
        if (isAfterTime(startDate, moment(), orgTimezone)) {
          showMsg(` Available by ${moment(startDate).format('MM/DD/YYYY hh:mm A')}`);
        } else {
          navigate(url);
        }
      } else if (endDate) {
        if (isBeforTime(endDate, moment(), orgTimezone)) {
          showMsg(` Exam is ended on ${moment(endDate).format('MM/DD/YYYY hh:mm A')}`);
        } else {
          navigate(url);
        }
      } else {
        navigate(url);
      }
    }
  };

  const dropdownMenu = {
    items: standardExams.map((exam) => ({
      key: `${exam.id}-grade-exams`,
      label: (
        <PaddedBtn onClick={() => handleSelectGrade(exam.displayName)}>
          {exam.displayName}
        </PaddedBtn>
      ),
    })),
  };

  // Filter exams based on selected grade and sort by order
  const filteredExams = activeGrade
    ? standardExams.find((exam) => exam.displayName === activeGrade)?.standard_exams || []
    : [];

  const { open, message } = state;

  const navigateToReport = () => {
      navigate('/standard-exam-report?grade=' + activeGrade);
  }

  return (
    <SentryWrap>
    <div className="basedCardsUpr">
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity="error">
          <strong>ALERT:</strong> {message}
        </Alert>
      </Snackbar>
      <div className="basedCardsBgColor relative">
      {currentUser?.role == 'admin' ? 
      <BackButtonEbook onClick={() => navigate(-1)} 
      style={{ position: "absolute", top: 0, left: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0, minWidth: 100 }} /> : null}
        <NavigationItem className="gradeLevellExam">
        <HeaderActionBar>
            <Dropdown
              menu={dropdownMenu}
              overlayClassName="gradeExamMenu"
              placement="bottomRight"
              trigger={['click']}
            >
              <ButtonLink onClick={(e) => e.preventDefault()}>
                {`Grade Level Exams (${activeGrade})` || 'Grade Level Exams'}
                <CaretDownOutlined />
              </ButtonLink>
            </Dropdown>
        
        {currentUser?.role === 'teacher' && (
              <ViewReportButton onClick={() => navigateToReport()}>
                        View Reports
                      </ViewReportButton>

        )}
            </HeaderActionBar>
            </NavigationItem>


        <Row className="gradeLevellExamRow" gutter={[32, 32]}>
          {filteredExams.map((stdExam, idx) => (
            <Col
              key={stdExam.id}
              xs={24}
              lg={8}
              md={8}
            >
              <div
              style={{ cursor: 'pointer' }}
                className={`basedCards ${idx % 3 === 1 ? 'basedCardsBg2' : idx % 3 === 2 ? 'basedCardsBg3' : ''}`}
                onClick={() => onCoverCLick(stdExam)}
              >
                <div className="basedCardsLbl">
                  <label>{activeGrade}</label>
                  <label>Standardized Exam</label>
                </div>
                <p>{stdExam.displayName}</p>
                <div className="basedCardsLogo">
                  <LogoIcon />
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    </div>
    </SentryWrap>
  );
};


//  ----------------------------------------------------------------------------
//  Redux
//  ----------------------------------------------------------------------------
const mapStateToProps = (state) => ({
  currentUser: state.currentUser,
  classActivities: state.classActivities,
  classQuizzes: state.classQuizzes,
  booksAllList: state.booksAllList,
});

export default connect(mapStateToProps, null)(StandardBasedExam);
